import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';

function QRCode({ url, size = 160 }: { url: string; size?: number }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&margin=2&color=0b0d0e&bgcolor=f4f4f4`;
  return (
    <div style={{ display: 'inline-block', padding: 8, background: '#f4f4f4', borderRadius: 8, border: '1px solid #e5e5e5' }}>
      <img src={src} alt="QR code" width={size} height={size} style={{ display: 'block' }} />
    </div>
  );
}

type Reg = { id: number; serialNumber: string; status: string; warrantyEndDate: string | null; purchaseDate: string | null; product: { name: string; modelNumber: string | null } | null };

const STATUS_COLORS: Record<string, string> = {
  active:      'bg-green-100 text-green-700',
  expired:     'bg-red-100 text-red-700',
  voided:      'bg-gray-100 text-gray-500',
  transferred: 'bg-blue-100 text-blue-700',
};

export default function RegistrationsPage() {
  const { t } = useTranslation();
  const [regs, setRegs]         = useState<Reg[]>([]);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    api.get<Reg[]>('/registrations').then(r => { setRegs(r); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const visible = regs.filter(r => {
    if (filter !== 'all' && r.status !== filter) return false;
    if (search && !r.serialNumber.toLowerCase().includes(search.toLowerCase()) &&
        !r.product?.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('registrations.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{regs.length} registered product{regs.length !== 1 ? 's' : ''}</p>
        </div>
        <Link to="/registrations/new" className="btn-primary">{t('registrations.registerNew')}</Link>
      </div>

      <div className="flex gap-3 mb-4">
        <input className="input max-w-xs" placeholder="Search by serial or product…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input w-36" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">{t('status.active')}</option>
          <option value="expired">{t('status.expired')}</option>
          <option value="voided">{t('status.voided')}</option>
        </select>
      </div>

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-gray-400">{t('common.loading')}</p>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-gray-400 text-sm">{t('registrations.none')}</p>
            <Link to="/registrations/new" className="btn-primary mt-4">{t('registrations.registerNew')}</Link>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="w-full text-sm" style={{ minWidth: 580 }}>
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wide">
                  <th className="px-6 py-3">{t('registrations.product')}</th>
                  <th className="px-6 py-3">{t('registrations.serial')}</th>
                  <th className="px-6 py-3">Purchase date</th>
                  <th className="px-6 py-3">{t('registrations.warrantyEnd')}</th>
                  <th className="px-6 py-3">{t('registrations.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.map(r => (
                  <>
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                      <td className="px-6 py-3 font-medium">{r.product?.name ?? '—'}<br/><span className="text-gray-400 font-normal text-xs">{r.product?.modelNumber}</span></td>
                      <td className="px-6 py-3 font-mono text-gray-600">{r.serialNumber}</td>
                      <td className="px-6 py-3 text-gray-500">{r.purchaseDate ?? '—'}</td>
                      <td className="px-6 py-3 text-gray-500">{r.warrantyEndDate ?? '—'}</td>
                      <td className="px-6 py-3">
                        <span className={`badge ${STATUS_COLORS[r.status] ?? 'bg-gray-100 text-gray-500'}`}>{t(`status.${r.status}`)}</span>
                      </td>
                    </tr>
                    {expandedId === r.id && (
                      <tr key={`${r.id}-qr`} style={{ background: '#fafafa' }}>
                        <td colSpan={5} className="px-6 py-4">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                            <div>
                              <p className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wider">Registration QR code</p>
                              <QRCode url={`${window.location.origin}/registrations/${r.id}`} size={160} />
                              <button className="btn-secondary text-xs mt-3"
                                onClick={e => { e.stopPropagation(); window.print(); }}>
                                Print QR
                              </button>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              <p>Scan to view warranty details for</p>
                              <p className="font-mono text-gray-600 mt-1">{r.serialNumber}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
